import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { InfoBox } from "../../components/InfoBox";
import { Input } from "../../components/Input";
import { useEmailPublicReceiptMutation } from "../../query/publicReceiptQuery";
import { ApiError } from "../../utils/api";

export const PublicReceiptEmailForm = ({
  hash,
  suggestedEmail,
}: {
  hash: string;
  suggestedEmail: string;
}) => {
  const { t } = useTranslation();
  const [emailSent, setEmailSent] = useState(false);
  const { mutateAsync: emailReceipt, error: emailError } =
    useEmailPublicReceiptMutation();

  const form = useForm({
    defaultValues: {
      email: suggestedEmail,
    },
    onSubmit: ({ value }) => {
      setEmailSent(false);
      return emailReceipt({ hash, email: value.email.trim() }).then(() => {
        setEmailSent(true);
      });
    },
  });

  const emailErrorMessage = !emailError
    ? null
    : emailError instanceof ApiError && emailError.statusCode === 400
      ? t("publicReceipt.emailInvalid")
      : emailError.message;

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        name="email"
        validators={{
          onChange: ({ value }) => (!value.trim() ? "required" : undefined),
        }}
      >
        {(field) => (
          <Input
            type="email"
            name="receipt-email"
            autoComplete="email"
            placeholder={t("publicReceipt.emailPlaceholder")}
            value={field.state.value}
            onChange={(event) => {
              field.handleChange(event.target.value);
              setEmailSent(false);
            }}
            onBlur={field.handleBlur}
          />
        )}
      </form.Field>
      {emailErrorMessage ? (
        <InfoBox variant="error">{emailErrorMessage}</InfoBox>
      ) : null}
      {emailSent ? (
        <InfoBox variant="success">{t("publicReceipt.emailSuccess")}</InfoBox>
      ) : null}
      <form.Subscribe
        selector={(state) =>
          [state.values.email, state.isSubmitting] as const
        }
      >
        {([email, isSubmitting]) => (
          <Button
            type="submit"
            $variant="outlined"
            disabled={isSubmitting || email.trim().length === 0}
          >
            {isSubmitting ? t("publicReceipt.sending") : t("publicReceipt.emailSend")}
          </Button>
        )}
      </form.Subscribe>
    </Form>
  );
};
